import { Box, Button, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle } from "react-icons/fi";
import { type BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { formatDecidedBy } from "../qualification/detail/formatDecidedBy";
import { useBidQualificationDecision } from "./useBidQualificationDecision";

interface Props {
  detail: BuyerBidV2DetailDto;
}

/**
 * The «kvalifisert» decision for a single-stage procedure. The negative outcome is deliberately
 * absent: disqualifying a supplier is an avvisning, and lives in the rejection flow next to this.
 * Decisions freeze once the qualification is completed (the event has opened its evaluation).
 *
 * Whether the decision applies at all follows the procedure, read off the event the page has
 * already loaded — the same derivation BidDetailView does for its two-stage tabs.
 */
export const BidQualificationDecision = ({ detail }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { applies, decision, decisionOpen, pending, decide, isLoading } =
    useBidQualificationDecision(detail);

  if (!applies) return null;

  if (decision) {
    return (
      <Stack spacing="1">
        <HStack spacing="2" color="green.700">
          <Icon as={FiCheckCircle} />
          <Text fontSize="sm" fontWeight="semibold">
            {t("Qualified")}
          </Text>
        </HStack>
        <Text fontSize="xs" color="smMuted">
          {formatDecidedBy(decision, displayDateWithTime, t)}
        </Text>
        {decisionOpen && (
          <Box>
            <Button
              variant="link"
              size="sm"
              colorScheme="teal"
              isLoading={isLoading}
              onClick={() => decide(false)}
            >
              {t("Undo the qualification")}
            </Button>
          </Box>
        )}
      </Stack>
    );
  }

  if (!decisionOpen) return null;

  return (
    <Stack spacing="1">
      <Button
        colorScheme="teal"
        width="full"
        isLoading={isLoading}
        isDisabled={pending > 0}
        onClick={() => decide(true)}
      >
        {t("Mark as qualified")}
      </Button>
      {pending > 0 && (
        <Text fontSize="xs" color="smMuted">
          {t("Review all qualification requirements first ({{count}} remaining)", {
            count: pending,
          })}
        </Text>
      )}
    </Stack>
  );
};
